// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Most pieces sold as antique that are not antique are not forgeries. They are honest reproductions that lost their paperwork, modern pieces made in an old style, or genuine old pieces that have been so heavily rebuilt that little of the original is left. Outright fakery is rarer than the internet suggests, and easier to catch than you would think.

Here is what to look at, in the order that settles it fastest.

## Start with the back

Turn the piece over. On genuine antique work the back is finished, because a jeweller who cut a stone by hand did not leave the underside rough. You should see:

- **Clean, deliberate metalwork.** Filed, chased, sometimes engraved even where nobody would look.
- **Settings that were built, not poured.** Individual claws with tool marks, a bezel burnished over the stone.
- **Solder joins.** Small, neat and slightly different in colour where separate parts were joined.

Casting from a mould gives itself away: soft, slightly rounded detail, a faintly grainy surface, tiny round pits from air bubbles, and identical repeats where a real hand would vary. Our [guide to handmade versus cast](/blog/handmade-vs-cast-jewelry) goes through the difference in detail.

## Findings are the hardest thing to fake convincingly

Clasps, catches, earring fittings and pin stems changed on a known timeline, and a reproduction usually uses whatever is cheap and available now.

- A **modern lobster clasp** on a chain sold as Victorian means the clasp is later. That may be a repair, which is fine, or it may be the whole story.
- A **rollover safety catch** on a brooch described as 1890 is wrong. Period brooches usually have a simple C hook and a pin that extends past the frame.
- **Butterfly earring backs** on a piece described as Georgian are wrong: those are twentieth century.

Findings prove the age of the finding, not the piece. But when everything on a piece is modern, the piece is modern.

## Stone cuts date themselves

A stone cut by hand for candlelight does not look like one cut by machine for a jeweller's spotlight. Modern round brilliants in a ring described as 1920s are a problem unless the seller says the stones were replaced.

Look for the open culet, the small flat at the point of the stone that reads as a tiny dot through the table. Our [guide to old cuts](/blog/old-mine-cut-vs-old-european-cut-diamonds) explains what to expect from each period.

The same logic runs through coloured stones. Calibre cut stones fitted exactly into their channels belong to the [Art Deco](/blog/art-deco-vs-art-nouveau-jewelry) period and take real work. Machine cut modern stones dropped into an approximate channel do not.

## Marks: absent, wrong, or too good

An **unmarked** piece is not suspicious on its own. Marking was not compulsory everywhere, marks wear away, and resizing removes them.

What is suspicious is a mark that does not fit:

- A **crisp, perfect hallmark** on a piece with a century of wear everywhere else. Marks wear at the same rate as the metal around them.
- A mark that is **stamped through** the wear rather than under it.
- **Purity claims that contradict the metal.** A piece marked 750 that behaves like base metal at the wear points.
- **British-looking marks in the wrong arrangement.** A genuine British assay set has a specific order and specific shapes. A single lion stamped alone is not a hallmark.

Photograph the mark and enlarge it rather than squinting. Our guides to [gold marks](/blog/how-to-tell-if-gold-jewelry-is-real) and [silver marks](/blog/silver-hallmarks-explained) cover what the real ones look like.

## Married pieces and converted pieces

This is far more common than forgery, and it is not dishonest when disclosed.

- A **married piece** combines parts from different objects: an old brooch pinned into a modern pendant mount, a period stone in a later setting.
- A **conversion** turns one thing into another: a brooch made into a pendant, a pocket watch chain shortened into a bracelet, earrings made from cufflinks.

Look for a bail that does not match the metal colour of the piece, solder that sits proud, a hinge or catch removed leaving a scar, or a design that reads oddly at one edge as though something was cut away.

Converted pieces are collectable and often lovely. They are worth less than untouched ones, and the description should say so.

## Weight and behaviour

Pick it up. Precious metal is dense, and a piece that looks substantial but feels like nothing is usually plated base metal or hollow.

A magnet rules things out quickly: gold, silver and platinum are not magnetic. A piece that jumps to a magnet has iron or steel in it. Not magnetic is not proof of anything, because brass is not magnetic either.

## The provenance test

Ask where it came from. Not to catch anyone out, but because the answer tells you how much the seller knows.

"Bought from a house clearance in Norfolk, no papers, I believe 1890s from the clasp" is a good answer. It is specific, it says what is known and what is guessed, and it can be checked against the piece.

"Certified antique, guaranteed Victorian" with no mark, no report and no history is a worse answer, because it claims certainty nobody has.

## Signed pieces need more care, not less

A signature multiplies value, which is exactly why signatures get added. If a piece is signed by a known maker:

- Compare the signature to documented examples. Real makers were consistent.
- Check the mark is **struck**, not engraved by hand afterwards, unless the maker did engrave.
- Ask whether the piece appears in any reference or archive.

For anything where the name carries most of the price, a specialist opinion is worth what it costs.

## What to ask before you buy

1. Photographs of the back, the findings and the mark, all in daylight.
2. Have any parts been replaced, and which?
3. Has it been converted from something else?
4. What is the weight in grams?
5. How do you date it, and what is that based on?

A seller who answers plainly, including the parts they do not know, is the seller to buy from. Our [gemstone guide](/blog/how-to-tell-if-a-gemstone-is-real) covers the equivalent questions for the stones.

Then buy where the money is protected. On Nextmine the payment is held until the piece has arrived and you have confirmed it, and a piece that is not as described has a defined process behind it rather than an argument. Read [how payments work](/how-payments-work), or [see what is listed](/s).

The Hallmark Society's guide to [telling real Art Deco from reproduction](https://hallmarksociety.com/art-deco-jewelry/) works through the same problem on one specific period, in more depth than a general guide can.
`;

export default body;
